// LE PLAN DE TRAVAIL (« Atelier ») — un grand plan, comme sur Miro.
//
// Les verbatims du projet y deviennent des post-it que l'on déplace à la main,
// que l'on regroupe, et dont les groupes donnent des insights ou se portent
// sur un persona. C'est le mode MANUEL de la synthèse : rien n'est décidé par
// l'IA, tout est rangé par la personne.
//
// Tout ce qui est posé ici est une VUE : retirer un post-it ne retire pas le
// verbatim, dissoudre un groupe ne défait ni l'insight ni le persona.
//
// Gestes :
//   · glisser le fond : sélectionner au lasso ; espace + glisser, ou bouton du
//     milieu : déplacer le plan ; molette : déplacer ; Ctrl + molette : zoomer ;
//   · glisser un post-it : déplacer la sélection ; Maj + clic : ajouter à la
//     sélection ; double-clic sur une note libre : modifier son texte ;
//   · lâcher un post-it dans le cadre d'un groupe l'y range ; le sortir du
//     cadre l'en retire — sauf si tout le groupe bouge ensemble ;
//   · Suppr : retirer la sélection du plan ; Échap : désélectionner.
//
// Sur TÉLÉPHONE, le plan se consulte : un doigt le déplace, deux doigts
// zooment. Ranger les post-it au doigt sur un petit écran serait laborieux ;
// cela se fait sur ordinateur.
//
// Les déplacements sont appliqués en OPTIMISTE sur le cache : un post-it lâché
// reste où il est, le serveur confirme derrière.
import {
  useCallback, useEffect, useMemo, useRef, useState,
  type FormEvent, type PointerEvent as RPointerEvent,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  boardKey, useAddBoardNotes, useBoard, useBoardDraft, useBoardGroupInsight, useCreateBoardGroup, useDeleteBoardGroup,
  useDeleteBoardNote, usePersonas, useStudy, useUpdateBoardGroup, useUpdateBoardNotes, type BoardNotePatch,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import type {
  Board, BoardClusterProposal, BoardGroup, BoardInsightBody, BoardNote, BoardVerbatim, Persona, ProblemType, RecoKind,
} from '../api/types';
import { AssistantPanel } from './AssistantPanel';
import { PROBLEM_TYPES, RECO_KINDS } from '../insights/labels';
import { Button, ErrorBanner, Eyebrow, Field, IconButton, Select, Spinner } from '../components/ui';
import { Icon } from '../components/icons';
import { Link } from '../router';
import { Drawer } from '../write/Drawer';
import { TextArea } from '../write/Forms';
import { useCanWrite } from '../write/useCanWrite';
import { useIsMobile, useNarrowScreen } from '../components/device';
import './board.css';

/** Taille d'un post-it, en unités du plan — la même que côté serveur. */
const NOTE_W = 220;
const NOTE_H = 150;
/** Marge du cadre d'un groupe autour de ses post-it, et hauteur de son en-tête. */
const PAD = 20;
const HEAD = 40;
const SEVERITIES = ['Bloquant', 'Plafond', 'Levier'];

interface View { x: number; y: number; s: number }
interface Rect { x: number; y: number; w: number; h: number }
interface Frame extends Rect { group: BoardGroup; notes: BoardNote[] }

type Gesture =
  | { kind: 'pan'; sx: number; sy: number; vx: number; vy: number }
  | { kind: 'pinch'; dist: number; mx: number; my: number; view: View }
  | { kind: 'lasso'; sx: number; sy: number; add: boolean }
  | { kind: 'drag'; ids: string[]; sx: number; sy: number; moved: boolean };

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const inside = (r: Rect, px: number, py: number) => px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h;
const overlaps = (a: Rect, b: Rect) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;

/** Le cadre de chaque groupe, déduit de ses post-it. Un groupe vide n'a pas de cadre. */
function framesOf(notes: BoardNote[], groups: BoardGroup[]): Map<string, Frame> {
  const out = new Map<string, Frame>();
  for (const g of groups) {
    const members = notes.filter((n) => n.groupId === g.id);
    if (members.length === 0) continue;
    const minX = Math.min(...members.map((n) => n.x));
    const minY = Math.min(...members.map((n) => n.y));
    const maxX = Math.max(...members.map((n) => n.x + NOTE_W));
    const maxY = Math.max(...members.map((n) => n.y + NOTE_H));
    out.set(g.id, {
      group: g, notes: members,
      x: minX - PAD, y: minY - PAD - HEAD, w: maxX - minX + 2 * PAD, h: maxY - minY + 2 * PAD + HEAD,
    });
  }
  return out;
}

/**
 * @param studyId  l'atelier D'UNE ÉTUDE : on n'y voit que les post-it de ce
 *                 terrain (et les notes libres écrites ici). Le plan reste le
 *                 même que celui du projet — c'est un filtre, pas une copie.
 */
export function BoardScreen({ projectId, studyId = null }: { projectId: string; studyId?: string | null }) {
  const board = useBoard(projectId);
  const study = useStudy(studyId);
  const personas = usePersonas(projectId);
  // Téléphone (mode mobile ET écran étroit) : lecture seule, gestes tactiles.
  const mobile = useIsMobile();
  const narrow = useNarrowScreen();
  const touchView = mobile && narrow;
  const canWrite = useCanWrite(projectId) && !touchView;
  const qc = useQueryClient();
  /** Les doigts posés sur le plan (téléphone) : un pour déplacer, deux pour zoomer. */
  const touches = useRef(new Map<number, { x: number; y: number }>());

  const addNotes = useAddBoardNotes(projectId);
  const updateNotes = useUpdateBoardNotes(projectId);
  const deleteNote = useDeleteBoardNote(projectId);
  const createGroup = useCreateBoardGroup(projectId);
  const updateGroup = useUpdateBoardGroup(projectId);
  const deleteGroup = useDeleteBoardGroup(projectId);
  const groupInsight = useBoardGroupInsight(projectId);

  const surfaceRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  const spaceHeld = useRef(false);
  const [view, setView] = useState<View>({ x: 80, y: 80, s: 1 });
  const [panning, setPanning] = useState(false);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [lasso, setLasso] = useState<Rect | null>(null);
  /** Décalage des post-it en cours de glisser, en unités du plan. */
  const [dragDelta, setDragDelta] = useState<{ ids: Set<string>; dx: number; dy: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [insightFor, setInsightFor] = useState<BoardGroup | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const data = board.data;
  // TOUS les post-it du plan, et ceux que l'on voit : dans l'atelier d'une
  // étude, seulement les siens. Ce qui modifie le cache, ou dissout un groupe
  // devenu vide, raisonne toujours sur TOUS — un post-it masqué existe encore.
  const allNotes = useMemo(() => data?.notes ?? [], [data]);
  const notes = useMemo(() => (studyId ? allNotes.filter((n) => n.studyId === studyId) : allNotes), [allNotes, studyId]);
  const groups = useMemo(() => data?.groups ?? [], [data]);
  const verbatimById = useMemo(() => new Map((data?.verbatims ?? []).map((v) => [v.id, v])), [data]);
  const onBoard = useMemo(() => new Set(allNotes.map((n) => n.verbatimId).filter(Boolean)), [allNotes]);
  const available = useMemo(
    () => (data?.verbatims ?? []).filter((v) => !onBoard.has(v.id) && (!studyId || v.studyId === studyId)),
    [data, onBoard, studyId],
  );

  /** Les post-it tels qu'ils s'affichent : la position de glisser incluse. */
  const shown = useMemo(() => {
    if (!dragDelta) return notes;
    return notes.map((n) => (dragDelta.ids.has(n.id) ? { ...n, x: n.x + dragDelta.dx, y: n.y + dragDelta.dy } : n));
  }, [notes, dragDelta]);
  const frames = useMemo(() => framesOf(shown, groups), [shown, groups]);

  // --- Repères ---------------------------------------------------------------
  const toWorld = useCallback((cx: number, cy: number) => {
    const r = surfaceRef.current?.getBoundingClientRect();
    const ox = r?.left ?? 0; const oy = r?.top ?? 0;
    return { x: (cx - ox - view.x) / view.s, y: (cy - oy - view.y) / view.s };
  }, [view]);

  /** Le centre de l'écran, en unités du plan — là où l'on dépose ce qui arrive. */
  const centerWorld = useCallback(() => {
    const r = surfaceRef.current?.getBoundingClientRect();
    const w = r?.width ?? 800; const h = r?.height ?? 600;
    return { x: (w / 2 - view.x) / view.s, y: (h / 2 - view.y) / view.s };
  }, [view]);

  const zoomAt = useCallback((cx: number, cy: number, factor: number) => {
    const r = surfaceRef.current?.getBoundingClientRect();
    const px = cx - (r?.left ?? 0); const py = cy - (r?.top ?? 0);
    setView((v) => {
      const s = clamp(v.s * factor, 0.15, 3);
      // Le point sous le curseur reste sous le curseur.
      return { s, x: px - ((px - v.x) / v.s) * s, y: py - ((py - v.y) / v.s) * s };
    });
  }, []);

  /** Cadre à l'écran une liste de post-it (positions en unités du plan). */
  const fitTo = useCallback((list: { x: number; y: number }[], maxScale = 1.25) => {
    const r = surfaceRef.current?.getBoundingClientRect();
    if (!r || list.length === 0) { setView({ x: 80, y: 80, s: 1 }); return; }
    const minX = Math.min(...list.map((n) => n.x)) - 60;
    const minY = Math.min(...list.map((n) => n.y)) - 80;
    const maxX = Math.max(...list.map((n) => n.x + NOTE_W)) + 60;
    const maxY = Math.max(...list.map((n) => n.y + NOTE_H)) + 60;
    const s = clamp(Math.min(r.width / (maxX - minX), r.height / (maxY - minY)), 0.15, maxScale);
    setView({ s, x: (r.width - (maxX - minX) * s) / 2 - minX * s, y: (r.height - (maxY - minY) * s) / 2 - minY * s });
  }, []);
  const fitAll = useCallback(() => fitTo(notes), [fitTo, notes]);

  /** Montrer des post-it : les sélectionner et les cadrer (ceux encore visibles). */
  const focusNotes = useCallback((ids: string[]) => {
    const list = notes.filter((n) => ids.includes(n.id));
    if (list.length === 0) return;
    setSelection(new Set(list.map((n) => n.id)));
    fitTo(list, 1);
  }, [notes, fitTo]);

  /** Le début du texte d'un post-it, pour que l'assistant cite sans tout recopier. */
  const excerpt = useCallback((id: string) => {
    const n = notes.find((x) => x.id === id);
    if (!n) return null;
    const t = (n.verbatimId ? verbatimById.get(n.verbatimId)?.text : n.text) ?? '';
    return t.length > 90 ? `${t.slice(0, 90)}…` : t;
  }, [notes, verbatimById]);

  // Dans l'atelier d'une étude, ses post-it peuvent être n'importe où sur le
  // plan : on les cadre à l'ouverture (une fois par étude, pas à chaque ajout).
  const fitted = useRef<string | null>(null);
  useEffect(() => {
    if (!studyId || notes.length === 0 || fitted.current === studyId) return;
    fitted.current = studyId;
    fitAll();
  }, [studyId, notes.length, fitAll]);

  // La molette : déplacer ; avec Ctrl (ou le pincement du pavé tactile) : zoomer.
  // Écouteur natif, non passif : React ne laisse pas empêcher le défilement.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // Dans un panneau (déposer des verbatims, créer un insight), la molette
      // fait défiler la liste — pas le plan.
      if (e.target instanceof HTMLElement && e.target.closest('dialog.zy-drawer, .board-assistant')) return;
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * 0.0015));
      else setView((v) => ({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAt]);

  // --- Écritures optimistes --------------------------------------------------
  const patchLocal = useCallback((patches: BoardNotePatch[]) => {
    const byId = new Map(patches.map((p) => [p.id, p]));
    qc.setQueryData<Board>(boardKey(projectId), (b) => (b ? {
      ...b,
      notes: b.notes.map((n) => {
        const p = byId.get(n.id);
        if (!p) return n;
        return {
          ...n,
          ...(p.x !== undefined ? { x: p.x } : {}),
          ...(p.y !== undefined ? { y: p.y } : {}),
          ...(p.groupId !== undefined ? { groupId: p.groupId } : {}),
          ...(p.text !== undefined ? { text: p.text } : {}),
        };
      }),
    } : b));
  }, [qc, projectId]);

  const apply = useCallback((patches: BoardNotePatch[]) => {
    if (patches.length === 0) return;
    patchLocal(patches);
    updateNotes.mutate(patches, { onError: (e) => setError(errorMessage(e)) });
  }, [patchLocal, updateNotes]);

  /** Les groupes qui n'auraient plus aucun post-it : on les dissout, un cadre
   *  sans post-it n'a nulle part où s'afficher. */
  const dropEmptyGroups = useCallback((remaining: BoardNote[]) => {
    for (const g of groups) {
      if (!remaining.some((n) => n.groupId === g.id)) deleteGroup.mutate(g.id);
    }
  }, [groups, deleteGroup]);

  const removeNotes = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const remaining = allNotes.filter((n) => !ids.includes(n.id));
    qc.setQueryData<Board>(boardKey(projectId), (b) => (b ? { ...b, notes: remaining } : b));
    for (const id of ids) deleteNote.mutate(id, { onError: (e) => setError(errorMessage(e)) });
    dropEmptyGroups(remaining);
    setSelection(new Set());
  }, [allNotes, qc, projectId, deleteNote, dropEmptyGroups]);

  // --- Gestes ---------------------------------------------------------------

  const beginDrag = (e: RPointerEvent, ids: string[]) => {
    gesture.current = { kind: 'drag', ids, sx: e.clientX, sy: e.clientY, moved: false };
    surfaceRef.current?.setPointerCapture(e.pointerId);
  };

  const onSurfaceDown = (e: RPointerEvent<HTMLDivElement>) => {
    if (editingId) return;
    const target = e.target as HTMLElement;
    // Les panneaux et la barre d'outils vivent DANS le plan : un clic sur une
    // case à cocher n'est pas le début d'un lasso. Sans ce garde, le plan
    // capturait le pointeur et le bouton « Tout cocher » ne recevait jamais son clic.
    if (target.closest('dialog.zy-drawer, .board-ui, .frame-head select, .frame-head input, .frame-head button, .frame-head a')) return;
    if (touchView && e.pointerType === 'touch') {
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      e.currentTarget.setPointerCapture(e.pointerId);
      const [a, b] = [...touches.current.values()];
      if (a && b) {
        gesture.current = {
          kind: 'pinch', dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
          mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, view,
        };
      } else {
        gesture.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
      }
      return;
    }
    if (e.button === 1 || (e.button === 0 && spaceHeld.current)) {
      e.preventDefault();
      gesture.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
      setPanning(true);
      e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }
    if (e.button !== 0) return;
    const noteEl = target.closest<HTMLElement>('.note');
    const headEl = target.closest<HTMLElement>('.frame-head');
    if (noteEl?.dataset['id']) {
      const id = noteEl.dataset['id'];
      let next: Set<string>;
      if (e.shiftKey) {
        next = new Set(selection);
        if (next.has(id)) next.delete(id); else next.add(id);
      } else {
        next = selection.has(id) ? selection : new Set([id]);
      }
      setSelection(next);
      if (canWrite && next.has(id)) beginDrag(e, [...next]);
      return;
    }
    if (headEl?.dataset['group']) {
      const frame = frames.get(headEl.dataset['group']);
      if (!frame) return;
      const ids = frame.notes.map((n) => n.id);
      setSelection(new Set(ids));
      if (canWrite) beginDrag(e, ids);
      return;
    }
    // Le fond : lasso.
    gesture.current = { kind: 'lasso', sx: e.clientX, sy: e.clientY, add: e.shiftKey };
    if (!e.shiftKey) setSelection(new Set());
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onSurfaceMove = (e: RPointerEvent<HTMLDivElement>) => {
    if (touches.current.has(e.pointerId)) touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current;
    if (!g) return;
    if (g.kind === 'pinch') {
      const [a, b] = [...touches.current.values()];
      if (!a || !b) return;
      const r = surfaceRef.current?.getBoundingClientRect();
      const ox = r?.left ?? 0; const oy = r?.top ?? 0;
      const s = clamp(g.view.s * (Math.hypot(a.x - b.x, a.y - b.y) / g.dist), 0.15, 3);
      const mx = (a.x + b.x) / 2 - ox; const my = (a.y + b.y) / 2 - oy;
      // Le point sous les doigts au départ suit les doigts.
      const wx = (g.mx - ox - g.view.x) / g.view.s; const wy = (g.my - oy - g.view.y) / g.view.s;
      setView({ s, x: mx - wx * s, y: my - wy * s });
    } else if (g.kind === 'pan') {
      setView((v) => ({ ...v, x: g.vx + (e.clientX - g.sx), y: g.vy + (e.clientY - g.sy) }));
    } else if (g.kind === 'lasso') {
      const r = surfaceRef.current?.getBoundingClientRect();
      const ox = r?.left ?? 0; const oy = r?.top ?? 0;
      setLasso({
        x: Math.min(g.sx, e.clientX) - ox, y: Math.min(g.sy, e.clientY) - oy,
        w: Math.abs(e.clientX - g.sx), h: Math.abs(e.clientY - g.sy),
      });
    } else {
      const dx = (e.clientX - g.sx) / view.s; const dy = (e.clientY - g.sy) / view.s;
      if (!g.moved && Math.hypot(e.clientX - g.sx, e.clientY - g.sy) < 3) return;
      g.moved = true;
      setDragDelta({ ids: new Set(g.ids), dx, dy });
    }
  };

  const onSurfaceUp = (e: RPointerEvent<HTMLDivElement>) => {
    if (touches.current.delete(e.pointerId)) {
      // Un doigt levé sur deux : on continue à déplacer avec celui qui reste.
      const [rest] = [...touches.current.values()];
      gesture.current = rest ? { kind: 'pan', sx: rest.x, sy: rest.y, vx: view.x, vy: view.y } : null;
      return;
    }
    const g = gesture.current;
    gesture.current = null;
    if (!g) return;
    if (g.kind === 'pinch') return;
    if (g.kind === 'pan') { setPanning(false); return; }
    if (g.kind === 'lasso') {
      if (lasso && (lasso.w > 4 || lasso.h > 4)) {
        const a = toWorld(lasso.x + (surfaceRef.current?.getBoundingClientRect().left ?? 0),
          lasso.y + (surfaceRef.current?.getBoundingClientRect().top ?? 0));
        const world: Rect = { x: a.x, y: a.y, w: lasso.w / view.s, h: lasso.h / view.s };
        const hit = notes.filter((n) => overlaps(world, { x: n.x, y: n.y, w: NOTE_W, h: NOTE_H })).map((n) => n.id);
        setSelection((s) => (g.add ? new Set([...s, ...hit]) : new Set(hit)));
      }
      setLasso(null);
      return;
    }
    // Fin d'un glisser : positions finales, et rangement dans les cadres.
    setDragDelta(null);
    if (!g.moved) return;
    const dx = Math.round((e.clientX - g.sx) / view.s); const dy = Math.round((e.clientY - g.sy) / view.s);
    const moving = new Set(g.ids);
    const others = notes.filter((n) => !moving.has(n.id));
    const stillFrames = framesOf(others, groups);
    const patches: BoardNotePatch[] = [];
    for (const n of notes) {
      if (!moving.has(n.id)) continue;
      const x = n.x + dx; const y = n.y + dy;
      const patch: BoardNotePatch = { id: n.id, x, y };
      // Tout le groupe bouge ensemble : il reste un groupe.
      const wholeGroup = n.groupId !== null && !others.some((o) => o.groupId === n.groupId);
      if (!wholeGroup) {
        const cx = x + NOTE_W / 2; const cy = y + NOTE_H / 2;
        let target: string | null = null;
        for (const f of stillFrames.values()) if (inside(f, cx, cy)) { target = f.group.id; break; }
        if (target !== n.groupId) patch.groupId = target;
      }
      patches.push(patch);
    }
    apply(patches);
    const after = allNotes.map((n) => {
      const p = patches.find((x) => x.id === n.id);
      return p && p.groupId !== undefined ? { ...n, groupId: p.groupId } : n;
    });
    dropEmptyGroups(after);
  };

  // Clavier : Suppr, Échap, espace (déplacer). Jamais pendant une saisie.
  useEffect(() => {
    const typing = (t: EventTarget | null) =>
      t instanceof HTMLElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName);
    const down = (e: KeyboardEvent) => {
      if (typing(e.target)) return;
      if (e.key === ' ') { spaceHeld.current = true; e.preventDefault(); }
      if (e.key === 'Escape') setSelection(new Set());
      if ((e.key === 'Delete' || e.key === 'Backspace') && canWrite && selection.size > 0) {
        e.preventDefault();
        removeNotes([...selection]);
      }
    };
    const up = (e: KeyboardEvent) => { if (e.key === ' ') spaceHeld.current = false; };
    document.addEventListener('keydown', down);
    document.addEventListener('keyup', up);
    return () => { document.removeEventListener('keydown', down); document.removeEventListener('keyup', up); };
  }, [selection, canWrite, removeNotes]);

  // --- Actions -----------------------------------------------------------------

  const dropVerbatims = (ids: string[]) => {
    const c = centerWorld();
    addNotes.mutate(
      { items: ids.map((verbatimId) => ({ verbatimId })), at: { x: c.x - 1.5 * (NOTE_W + 24), y: c.y - NOTE_H } },
      { onSuccess: () => setAdding(false), onError: (e) => setError(errorMessage(e)) },
    );
  };

  const addFreeNote = () => {
    const c = centerWorld();
    addNotes.mutate(
      { items: [{ text: 'Nouvelle note', x: c.x - NOTE_W / 2, y: c.y - NOTE_H / 2 }], ...(studyId ? { studyId } : {}) },
      { onSuccess: (r) => { const n = r.notes[0]; if (n) { setSelection(new Set([n.id])); setEditingId(n.id); } },
        onError: (e) => setError(errorMessage(e)) },
    );
  };

  const groupSelection = () => {
    const ids = [...selection];
    if (ids.length === 0) return;
    createGroup.mutate(
      { name: `Groupe ${groups.length + 1}`, noteIds: ids },
      { onSuccess: () => {
        // Les anciens groupes de ces post-it peuvent s'être vidés.
        dropEmptyGroups(allNotes.map((n) => (selection.has(n.id) ? { ...n, groupId: 'new' } : n)));
      }, onError: (e) => setError(errorMessage(e)) },
    );
  };

  /**
   * Appliquer un regroupement proposé par l'assistant : ses post-it sont
   * rapprochés en grille à droite de tout le plan (un cadre de groupe se
   * dessine autour de ses post-it — dispersés, il couvrirait tout), puis
   * regroupés sous le nom proposé.
   */
  const applyCluster = async (p: BoardClusterProposal) => {
    const members = notes.filter((n) => p.noteIds.includes(n.id) && !n.groupId);
    if (members.length < 2) { setError('Ces post-it ont déjà été rangés entre-temps.'); return; }
    const right = allNotes.length ? Math.max(...allNotes.map((n) => n.x)) + NOTE_W + 160 : 0;
    const top = allNotes.length ? Math.min(...allNotes.map((n) => n.y)) : 0;
    const COLS = 4;
    const patches: BoardNotePatch[] = members.map((n, i) => ({
      id: n.id, x: right + (i % COLS) * (NOTE_W + 24), y: top + HEAD + Math.floor(i / COLS) * (NOTE_H + 24),
    }));
    apply(patches);
    try {
      await createGroup.mutateAsync({ name: p.name, noteIds: members.map((n) => n.id) });
    } catch (e) {
      setError(errorMessage(e));
      return;
    }
    setSelection(new Set(members.map((n) => n.id)));
    fitTo(patches.map((x) => ({ x: x.x ?? 0, y: x.y ?? 0 })), 1);
  };

  const ungroupSelection = () => {
    const patches = notes.filter((n) => selection.has(n.id) && n.groupId).map((n) => ({ id: n.id, groupId: null }));
    apply(patches);
    dropEmptyGroups(allNotes.map((n) => (selection.has(n.id) ? { ...n, groupId: null } : n)));
  };

  const saveText = (id: string, text: string) => {
    setEditingId(null);
    const t = text.trim();
    if (!t) return;
    apply([{ id, text: t }]);
  };

  // --- Rendu ----------------------------------------------------------------

  if (board.isPending) {
    return <div className="board board-empty"><Spinner label="Chargement du plan de travail…" /></div>;
  }
  if (board.isError) {
    return (
      <div className="board board-empty">
        <ErrorBanner>{errorMessage(board.error)}</ErrorBanner>
      </div>
    );
  }

  const selectedInGroup = notes.some((n) => selection.has(n.id) && n.groupId);
  const personaList: Persona[] = personas.data ?? [];

  return (
    <div
      ref={surfaceRef}
      className={`board${panning ? ' board-panning' : ''}${canWrite ? '' : ' board-ro'}`}
      onPointerDown={onSurfaceDown}
      onPointerMove={onSurfaceMove}
      onPointerUp={onSurfaceUp}
      onPointerCancel={onSurfaceUp}
      onDoubleClick={(e) => {
        const el = (e.target as HTMLElement).closest<HTMLElement>('.note-free');
        if (canWrite && el?.dataset['id']) setEditingId(el.dataset['id']);
      }}
    >
      <div className="board-layer" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})` }}>
        {[...frames.values()].map((f) => (
          <GroupFrame
            key={f.group.id}
            frame={f}
            projectId={projectId}
            personas={personaList}
            canWrite={canWrite}
            onRename={(name) => updateGroup.mutate({ id: f.group.id, patch: { name } }, { onError: (e) => setError(errorMessage(e)) })}
            onPersona={(personaId) => updateGroup.mutate({ id: f.group.id, patch: { personaId } }, { onError: (e) => setError(errorMessage(e)) })}
            onInsight={() => setInsightFor(f.group)}
            onDissolve={() => deleteGroup.mutate(f.group.id, { onError: (e) => setError(errorMessage(e)) })}
          />
        ))}
        {shown.map((n) => (
          <StickyNote
            key={n.id}
            note={n}
            verbatim={n.verbatimId ? verbatimById.get(n.verbatimId) : undefined}
            selected={selection.has(n.id)}
            editing={editingId === n.id}
            onSave={(t) => saveText(n.id, t)}
            onCancel={() => setEditingId(null)}
          />
        ))}
      </div>

      {lasso ? <div className="lasso" style={{ left: lasso.x, top: lasso.y, width: lasso.w, height: lasso.h }} /> : null}

      {/* — Barre d'outils — */}
      <div className="board-ui board-toolbar">
        <div className="board-title">
          <Eyebrow>{studyId ? `Atelier · ${study.data?.code ?? studyId}` : 'Atelier'}</Eyebrow>
          <span className="t-mono board-count">
            {notes.length} post-it · {frames.size} groupe{frames.size > 1 ? 's' : ''}
          </span>
        </div>
        {studyId ? (
          <span className="board-scope">
            {study.data?.objective ? <span className="board-scope-text" title={study.data.objective}>{study.data.objective}</span> : null}
            <Link to={`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(studyId)}`} className="board-scope-link">Retour à l’étude</Link>
            <Link to={`/projet/${encodeURIComponent(projectId)}/atelier`} className="board-scope-link">Tout le projet</Link>
          </span>
        ) : null}
        {canWrite ? (
          <>
            <Button variant="accent" onClick={() => setAdding(true)} disabled={available.length === 0}>
              Déposer des verbatims{available.length ? ` (${available.length})` : ''}
            </Button>
            <Button variant="ghost" onClick={addFreeNote} loading={addNotes.isPending}>
              Note libre
            </Button>
            <Button variant={assistantOpen ? 'primary' : 'ghost'} onClick={() => setAssistantOpen((v) => !v)} aria-pressed={assistantOpen}>
              <Icon name="sparkles" size={14} /> Assistant IA
            </Button>
          </>
        ) : (
          <span className="board-ro-note">
            {touchView ? 'Lecture seule sur téléphone : rangez les post-it sur ordinateur' : 'Lecture seule'}
          </span>
        )}
        <Button variant="ghost" onClick={fitAll}>Tout voir</Button>
      </div>

      <div className="board-ui board-zoom" role="group" aria-label="Zoom">
        <button type="button" className="board-zoom-btn" aria-label="Zoom arrière" onClick={() => zoomAt(window.innerWidth / 2, window.innerHeight / 2, 0.8)}>−</button>
        <span className="t-mono">{Math.round(view.s * 100)} %</span>
        <button type="button" className="board-zoom-btn" aria-label="Zoom avant" onClick={() => zoomAt(window.innerWidth / 2, window.innerHeight / 2, 1.25)}>+</button>
      </div>

      {canWrite && selection.size > 0 ? (
        <div className="board-ui board-selbar" role="toolbar" aria-label="Sélection">
          <span className="t-mono">{selection.size} sélectionné{selection.size > 1 ? 's' : ''}</span>
          <Button variant="primary" onClick={groupSelection} loading={createGroup.isPending}>
            Regrouper
          </Button>
          {selectedInGroup ? (
            <Button variant="ghost" onClick={ungroupSelection}>Sortir du groupe</Button>
          ) : null}
          <Button variant="ghost" onClick={() => removeNotes([...selection])}>Retirer du plan</Button>
          <IconButton icon="close" label="Désélectionner" onClick={() => setSelection(new Set())} />
        </div>
      ) : null}

      {notes.length === 0 ? (
        <div className="board-ui board-hint">
          <h2 className="t-h3">Le plan est vide</h2>
          <p>
            {canWrite
              ? studyId
                ? 'Déposez le terrain de cette étude : chaque verbatim devient un post-it. Regroupez-les à la main, puis tirez un insight de chaque groupe.'
                : 'Déposez les verbatims de vos études : chacun devient un post-it. Regroupez-les à la main, puis tirez un insight de chaque groupe.'
              : touchView
                ? 'Rien n’est encore posé sur ce plan. Les post-it se déposent et se rangent sur ordinateur.'
                : 'Personne n’a encore rien posé sur ce plan.'}
          </p>
          {canWrite && available.length > 0 ? (
            <Button variant="ghost" onClick={() => setAdding(true)}>Déposer des verbatims ({available.length})</Button>
          ) : null}
          {canWrite && available.length === 0 ? (
            <p className="zy-texte-muet">
              {studyId
                ? 'Aucun verbatim dans cette étude pour l’instant : captez d’abord un entretien ou importez une transcription.'
                : 'Aucun verbatim dans ce projet pour l’instant : ils viennent des entretiens et des études.'}
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="board-ui board-error" role="alert">
          <ErrorBanner>{error}</ErrorBanner>
          <Button variant="ghost" onClick={() => setError(null)}>Fermer</Button>
        </div>
      ) : null}

      <Drawer
        open={adding}
        title="Déposer des verbatims"
        description="Chaque verbatim coché devient un post-it, posé au centre du plan. Un verbatim déjà posé n’apparaît plus ici."
        onClose={() => setAdding(false)}
      >
        <AddVerbatims verbatims={available} pending={addNotes.isPending} onAdd={dropVerbatims} />
      </Drawer>

      <Drawer
        open={insightFor !== null}
        title="Tirer un insight du groupe"
        description={insightFor ? `Les verbatims de « ${insightFor.name || 'ce groupe'} » en seront les preuves.` : undefined}
        onClose={() => setInsightFor(null)}
      >
        {insightFor ? (
          <InsightForm
            key={insightFor.id}
            projectId={projectId}
            group={insightFor}
            canAssist={canWrite}
            count={frames.get(insightFor.id)?.notes.filter((n) => n.verbatimId).length ?? 0}
            pending={groupInsight.isPending}
            error={groupInsight.error}
            onRename={(name) => updateGroup.mutate({ id: insightFor.id, patch: { name } }, { onError: (e) => setError(errorMessage(e)) })}
            onSubmit={(body) => groupInsight.mutate({ groupId: insightFor.id, body }, { onSuccess: () => setInsightFor(null) })}
          />
        ) : null}
      </Drawer>

      {canWrite && assistantOpen ? (
        <AssistantPanel
          projectId={projectId}
          studyId={studyId}
          excerpt={excerpt}
          onFocus={focusNotes}
          onApply={applyCluster}
          onClose={() => setAssistantOpen(false)}
        />
      ) : null}
    </div>
  );
}

// --- Un post-it -------------------------------------------------------------

function StickyNote({
  note, verbatim, selected, editing, onSave, onCancel,
}: {
  note: BoardNote;
  verbatim: BoardVerbatim | undefined;
  selected: boolean;
  editing: boolean;
  onSave: (text: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(note.text ?? '');
  useEffect(() => { if (editing) setDraft(note.text ?? ''); }, [editing, note.text]);
  const free = note.verbatimId === null;
  const tone = verbatim?.sentiment ? ` note-${verbatim.sentiment}` : '';
  const who = verbatim?.personaName ?? verbatim?.speaker ?? null;
  return (
    <div
      className={`note${free ? ' note-free' : tone}${selected ? ' note-on' : ''}`}
      style={{ transform: `translate(${note.x}px, ${note.y}px)` }}
      data-id={note.id}
      role="group"
      aria-label={free ? 'Note libre' : 'Verbatim'}
    >
      {editing ? (
        <textarea
          className="note-edit"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onSave(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onSave(draft); }
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      ) : (
        <p className="note-text">{free ? note.text : verbatim?.text ?? '…'}</p>
      )}
      <div className="note-foot">
        <span className="note-who">{free ? 'Note' : who ?? 'Anonyme'}</span>
        <span>{free ? '' : verbatim?.studyCode ?? verbatim?.sourceRef ?? ''}</span>
      </div>
    </div>
  );
}

// --- Le cadre d'un groupe ------------------------------------------------------

function GroupFrame({
  frame, projectId, personas, canWrite, onRename, onPersona, onInsight, onDissolve,
}: {
  frame: Frame;
  projectId: string;
  personas: Persona[];
  canWrite: boolean;
  onRename: (name: string) => void;
  onPersona: (personaId: string | null) => void;
  onInsight: () => void;
  onDissolve: () => void;
}) {
  const { group } = frame;
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(group.name);
  useEffect(() => { setName(group.name); }, [group.name]);
  const persona = personas.find((p) => p.id === group.personaId);
  const withVerbatims = frame.notes.filter((n) => n.verbatimId).length;

  return (
    <div className="frame" style={{ transform: `translate(${frame.x}px, ${frame.y}px)`, width: frame.w, height: frame.h }}>
      <div className="frame-head" data-group={group.id}>
        {renaming ? (
          <input
            className="frame-name-input"
            value={name}
            autoFocus
            aria-label="Nom du groupe"
            onChange={(e) => setName(e.target.value)}
            onBlur={() => { setRenaming(false); if (name.trim() !== group.name) onRename(name.trim()); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') { setName(group.name); setRenaming(false); }
            }}
          />
        ) : (
          <button
            type="button"
            className="frame-name"
            onClick={() => { if (canWrite) setRenaming(true); }}
            title={canWrite ? 'Renommer' : undefined}
          >
            {group.name || 'Sans nom'}
          </button>
        )}
        <span className="t-mono frame-count">{frame.notes.length}</span>
        <span className="frame-spacer" />
        {canWrite ? (
          <Select
            className="frame-persona"
            aria-label="Rattacher à un persona"
            value={group.personaId ?? ''}
            onChange={(e) => onPersona(e.target.value || null)}
            title="Rattacher ce groupe à un persona : ses verbatims lui sont attribués"
          >
            <option value="">Persona…</option>
            {personas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        ) : persona ? (
          <span className="frame-chip">{persona.name}</span>
        ) : null}
        {group.insightId ? (
          <Link to={`/projet/${encodeURIComponent(projectId)}/insights`} className="frame-chip frame-insight">
            <Icon name="bulb" size={12} /> Insight
          </Link>
        ) : canWrite ? (
          <button type="button" className="frame-action" onClick={onInsight} disabled={withVerbatims === 0}
            title={withVerbatims === 0 ? 'Il faut au moins un verbatim dans le groupe' : 'Tirer un insight de ce groupe'}>
            <Icon name="bulb" size={12} /> Insight
          </button>
        ) : null}
        {canWrite ? (
          <IconButton icon="close" label="Dissoudre le groupe" onClick={onDissolve} />
        ) : null}
      </div>
    </div>
  );
}

// --- Déposer des verbatims --------------------------------------------------------

function AddVerbatims({ verbatims, pending, onAdd }: {
  verbatims: BoardVerbatim[]; pending: boolean; onAdd: (ids: string[]) => void;
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const byStudy = useMemo(() => {
    const m = new Map<string, BoardVerbatim[]>();
    for (const v of verbatims) {
      const k = v.studyCode ?? 'Sans étude';
      m.set(k, [...(m.get(k) ?? []), v]);
    }
    return [...m.entries()];
  }, [verbatims]);
  const toggle = (id: string) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const all = picked.size === verbatims.length && verbatims.length > 0;

  if (verbatims.length === 0) return <p className="zy-texte-muet">Tous les verbatims du projet sont déjà sur le plan.</p>;
  return (
    <div className="write-form">
      <div className="m0-actions m0-actions-left">
        <Button variant="ghost" onClick={() => setPicked(all ? new Set() : new Set(verbatims.map((v) => v.id)))}>
          {all ? 'Tout décocher' : 'Tout cocher'}
        </Button>
        <Button variant="accent" loading={pending} disabled={picked.size === 0} onClick={() => onAdd([...picked])}>
          Déposer {picked.size > 0 ? `(${picked.size})` : ''}
        </Button>
      </div>
      {byStudy.map(([code, list]) => (
        <section key={code} className="pick-study">
          <p className="zy-field__label">{code} <span className="t-mono">{list.length}</span></p>
          <ul className="pick-list">
            {list.map((v) => (
              <li key={v.id}>
                <label className={`pick-row${v.sentiment ? ` note-${v.sentiment}` : ''}`}>
                  <input className="zy-check" type="checkbox" checked={picked.has(v.id)} onChange={() => toggle(v.id)} />
                  <span className="pick-text">{v.text}</span>
                  <span className="pick-who t-mono">{v.personaName ?? v.speaker ?? ''}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// --- Tirer un insight d'un groupe ----------------------------------------------------

function InsightForm({ projectId, group, canAssist, count, pending, error, onRename, onSubmit }: {
  projectId: string;
  group: BoardGroup;
  canAssist: boolean;
  count: number;
  pending: boolean;
  error: unknown;
  onRename: (name: string) => void;
  onSubmit: (body: BoardInsightBody) => void;
}) {
  const draft = useBoardDraft(projectId);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [why, setWhy] = useState('');
  const [opportunity, setOpportunity] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [recoKind, setRecoKind] = useState<RecoKind | ''>('');
  const [severity, setSeverity] = useState('');
  const [problemType, setProblemType] = useState<ProblemType | ''>('');
  /** Le nom de groupe proposé par l'IA, tant qu'on ne l'a ni pris ni ignoré. */
  const [suggestedName, setSuggestedName] = useState<string | null>(null);
  /** Le brouillon de l'IA a servi : l'insight en gardera la marque. */
  const [fromAi, setFromAi] = useState(false);

  // L'IA REMPLIT le formulaire, elle n'enregistre rien : on relit, on corrige,
  // puis on crée l'insight comme d'habitude.
  const prefill = () => draft.mutate(group.id, {
    onSuccess: ({ draft: d }) => {
      setTitle(d.title ?? '');
      setSummary(d.summary ?? '');
      setWhy(d.why ?? '');
      setOpportunity(d.opportunity ?? '');
      setRecommendation(d.recommendation ?? '');
      setRecoKind(d.recommendationKind ?? '');
      setSeverity(d.severity ?? '');
      setProblemType(d.problemType ?? '');
      setSuggestedName(d.groupName && d.groupName !== group.name ? d.groupName : null);
      setFromAi(true);
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const reco = recommendation.trim();
    onSubmit({
      title: title.trim(),
      ...(summary.trim() ? { summary: summary.trim() } : {}),
      ...(why.trim() ? { why: why.trim() } : {}),
      ...(opportunity.trim() ? { opportunity: opportunity.trim() } : {}),
      ...(reco ? { recommendation: reco } : {}),
      ...(reco && recoKind ? { recommendationKind: recoKind } : {}),
      ...(severity ? { severity } : {}),
      ...(problemType ? { problemType } : {}),
      origin: fromAi ? 'ia' : 'manuel',
    });
  };
  return (
    <form className="write-form" onSubmit={submit} noValidate>
      <p className="m0-note">
        {count} verbatim{count > 1 ? 's' : ''} du groupe {count > 1 ? 'seront cités' : 'sera cité'} comme preuve{count > 1 ? 's' : ''}.
        Un insight sans preuve est refusé par le serveur : c’est ce qui le rend opposable.
      </p>
      {canAssist ? (
        <div className="assistant-prefill">
          <Button type="button" variant="ghost" onClick={prefill} loading={draft.isPending}>
            <Icon name="sparkles" size={14} /> {fromAi ? 'Préparer à nouveau avec l’IA' : 'Préparer avec l’IA'}
          </Button>
          <span className="assistant-hint">L’IA relit les post-it du groupe et remplit le formulaire. Vous relisez avant de créer.</span>
          {draft.data?.mode === 'fake' ? <span className="assistant-mode">Démonstration locale, sans modèle.</span> : null}
        </div>
      ) : null}
      {draft.error ? <ErrorBanner>{errorMessage(draft.error)}</ErrorBanner> : null}
      {suggestedName ? (
        <p className="assistant-rename">
          Nom proposé pour le groupe : <strong>{suggestedName}</strong>
          <Button variant="link" size="s" type="button" onClick={() => { onRename(suggestedName); setSuggestedName(null); }}>Renommer</Button>
          <Button variant="link" size="s" type="button" onClick={() => setSuggestedName(null)}>Garder le nom actuel</Button>
        </p>
      ) : null}
      <Field label="Titre *" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Les gants font rater la saisie" />
      <TextArea label="Ce que l’on constate" value={summary} onChange={setSummary} rows={3} placeholder="En quelques phrases, ce que disent ces verbatims." />
      <TextArea label="Pourquoi (interprétation)" value={why} onChange={setWhy} rows={3} placeholder="Pourquoi les utilisateurs se comportent ainsi." />
      <TextArea label="Opportunité" value={opportunity} onChange={setOpportunity} rows={2} placeholder="Ce qui améliorerait leur expérience." />
      <TextArea label="Recommandation" value={recommendation} onChange={setRecommendation} rows={2} placeholder="Une action précise : quoi changer, et où." />
      <div className="field-row">
        <div className="zy-field">
          <label className="zy-field__label" htmlFor="board-reco-kind">Nature de la recommandation</label>
          <Select id="board-reco-kind" value={recoKind} disabled={!recommendation.trim()} onChange={(e) => setRecoKind(e.target.value as RecoKind | '')}>
            <option value="">—</option>
            {RECO_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
        </div>
        <div className="zy-field">
          <label className="zy-field__label" htmlFor="board-severity">Sévérité</label>
          <Select id="board-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="">À préciser</option>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </div>
        <div className="zy-field">
          <label className="zy-field__label" htmlFor="board-problem">Type de problème</label>
          <Select id="board-problem" value={problemType} onChange={(e) => setProblemType(e.target.value as ProblemType | '')}>
            <option value="">À préciser</option>
            {PROBLEM_TYPES.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </Select>
        </div>
      </div>
      {error ? <ErrorBanner>{errorMessage(error)}</ErrorBanner> : null}
      <div className="m0-actions">
        <Button type="submit" variant="accent" loading={pending} disabled={!title.trim()}>Créer l’insight</Button>
      </div>
    </form>
  );
}
